import { authFetch } from './authFetch';

/**
 * Billing API client (buy-loop Inc 4). The direct `/billing/checkout` route was RETIRED
 * (it bypassed the inctasoft-cf edge + Topology-W чл.52о compliance). Brand subscription
 * creation now flows through `POST /billing/buy-intent` → a signed token → the edge, which
 * returns EITHER a checkout redirect OR (already-subscribed) the Customer Portal URL.
 */

export type Brand = 'valko' | 'protofac' | 'siynergia';

export interface BillingStatus {
  tier: string;
  hasSubscription: boolean;
  stripeCustomerId: string | null;
  /** The deployment's product brand (§D2) — which brand the "Upgrade" buttons transact. */
  brand: Brand;
}

/** Result of a mint: either a Stripe Checkout redirect, or the Customer Portal (already subscribed). */
export interface BuyIntentRedirect {
  mode: 'checkout' | 'portal';
  url: string;
}

export async function getBillingStatus(): Promise<BillingStatus> {
  const res = await authFetch('/api/billing/status');
  if (!res.ok) throw new Error(`Failed to load billing status: ${res.status}`);
  return res.json();
}

/**
 * Mint a signed buy-intent for `brand`/`tier` and return the edge redirect (or portal) URL.
 * `tier` is Phase-1 metadata-only — the edge resolves the price server-side from the brand.
 */
export async function createBuyIntent(brand: string, tier: string): Promise<BuyIntentRedirect> {
  const res = await authFetch('/api/billing/buy-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ brand, tier }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Buy-intent failed: ${res.status}`);
  }
  return res.json();
}

export async function createPortal(): Promise<{ portalUrl: string }> {
  const res = await authFetch('/api/billing/portal', { method: 'POST' });
  if (!res.ok) throw new Error(`Failed to open billing portal: ${res.status}`);
  return res.json();
}

/** Valko credits SKU (prepaid top-up packs). Mirrors the backend allowlist + edge tokenBuyable set. */
export type CreditsProduct =
  | 'valko_credits_5'
  | 'valko_credits_15'
  | 'valko_credits_40'
  | 'valko_credits_100';

/**
 * Mint a signed buy-intent for a prepaid-credits top-up pack and return the edge checkout redirect.
 * The credits path is ALWAYS `mode:'checkout'` (a top-up is never a parallel subscription, so it
 * skips the already-subscribed portal redirect). Shares the `/billing/buy-intent` endpoint with the
 * subscription path — the `{ product }` body selects the credits branch (v2 token) server-side.
 */
export async function createCreditsBuyIntent(product: CreditsProduct): Promise<{ mode: 'checkout'; url: string }> {
  const res = await authFetch('/api/billing/buy-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Top-up failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Convenience for callers that don't already hold the deployment brand: resolve it from
 * billing status, then mint. (SettingsModal already has `billingStatus.brand`, so it calls
 * `createBuyIntent` directly and skips this extra round-trip.)
 */
export async function startDeploymentCheckout(tier: string): Promise<BuyIntentRedirect> {
  const status = await getBillingStatus();
  return createBuyIntent(status.brand, tier);
}

// ════════════════════════════════════════════════════════════════════════════
// Customer Portal read surfaces (valko-credits-platform Phase 3)
//
// User-scoped, read-only views over the append-only credit_ledger + usage. These
// power PortalHistoryTab / PortalUsageTab / PortalReceiptsTab. They are the ONLY
// new data surfaces of the portal — everything else composes existing Phase-2
// pieces (creditsStore selectors, TopUpModal, UsageTab, createPortal).
//
// The backend read endpoints live on the CreditsPortalController at
// `GET /billing/credits/{history,receipts,usage,balance}` — user-scoped (req.user.uid),
// GoogleAuthGuard-protected, brand-scoped server-side. Nginx strips `/api/`, so the
// client calls `/api/billing/credits/*` → backend sees `billing/credits/*` (matches the
// existing `/api/billing/*` convention). These TYPES MIRROR the backend view types in
// credits-portal.service.ts (the authoritative, SE-pinned shapes) — keep them in sync.
// ════════════════════════════════════════════════════════════════════════════

/**
 * The Stripe-computed tax of one purchase (Stripe Tax adoption Phase 3; mirrors backend `TaxLineView` in
 * billing/portal-tax-line.ts). Display-only: every figure is Stripe's own computation as VA persisted it in
 * `stripe_tax_record` — the client never derives a rate. `tax` is null/absent for a row that is not a sale or has no
 * recorded tax fact (a pre-P2 purchase), and absent entirely on an older backend (frontend-first deploy is safe).
 */
export interface CreditTaxLine {
  /** Stripe's `total_details.amount_tax`, minor units of `currency` — the VAT INCLUDED in the gross. 0 is a real value. */
  amountTaxMinor: number | null;
  /** Lower-case ISO 4217 (`eur`). */
  currency: string | null;
  /** The buyer's Stripe-collected billing country (ISO 3166-1 alpha-2). */
  country: string | null;
  /** Stripe's own rate for the sale, or null when Stripe stated none — never derived here. */
  ratePercent: number | null;
  /** Stripe's `taxability_reason` (e.g. standard_rated, not_collecting, reverse_charge). */
  taxabilityReason: string | null;
}

/** One append-only credit_ledger row (mirrors backend HistoryRow). */
export interface CreditHistoryRow {
  id: string;
  /** topup | bonus | grant_signup | grant_daily | hold | release | burn | expiry | refund_clawback. */
  entryType: string;
  /** Signed centicredits for this row (1 credit = 100 centi). */
  centicredits: number;
  lotId: string;
  parentLotId: string | null;
  /** Fiscal EUR gross on topup/refund rows (string decimal), else null. */
  eurGross: string | null;
  orderNumber: string | null;
  receiptRef: string | null;
  /** 12-month lot expiry (ISO) on lot-origin rows. */
  expiresAt: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
  /** Stripe Tax P3: the Stripe-computed tax of this purchase (paid top-up rows only); null/absent otherwise. */
  tax?: CreditTaxLine | null;
}

/** Keyset-paginated history page (mirrors backend HistoryPage). */
export interface CreditsHistoryPage {
  rows: CreditHistoryRow[];
  nextCursor: string | null;
  perLotExpiry: Array<{ lotId: string; lotType: string; remainderCenti: number; expiresAt: string | null }>;
}

/** A fiscal top-up receipt row (mirrors backend Receipt). */
export interface CreditReceipt {
  orderNumber: string | null;
  receiptRef: string | null;
  /** Fiscal EUR gross (string decimal), the amount actually paid. */
  eurGross: string | null;
  country: string | null;
  createdAt: string;
  lotId: string;
  /** Stripe Tax P3: the Stripe-computed tax of this purchase; null/absent when nothing was recorded. */
  tax?: CreditTaxLine | null;
}

export interface CreditsReceiptsResponse {
  receipts: CreditReceipt[];
}

/** Fetch the current user's append-only credit ledger page (newest first). */
export async function getCreditsHistory(opts?: { limit?: number; cursor?: string | null }): Promise<CreditsHistoryPage> {
  const qs = new URLSearchParams();
  if (opts?.limit != null) qs.set('limit', String(opts.limit));
  if (opts?.cursor) qs.set('cursor', opts.cursor);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authFetch(`/api/billing/credits/history${suffix}`);
  if (!res.ok) throw new Error(`Failed to load credits history: ${res.status}`);
  return res.json();
}

/** Server-authoritative balance view (mirrors backend BalanceView). With `?since`, also
 *  carries the topup-confirmation backstop fact (E2 tail b, PR B). */
export interface CreditsBalanceView {
  balanceCenti: number;
  heldCenti: number;
  availableCenti: number;
  live: boolean;
  perLot: Array<{ lotId: string; lotType: string; remainderCenti: number; expiresAt: string | null }>;
  /** Present only when `?since` was passed. SUM of topup centicredits after `since`. `>0` fires the notice. */
  topupSinceCenti?: number;
  /** Latest topup createdAt (ISO) after `since`, else null. Present only with `?since`. */
  lastTopupTs?: string | null;
}

/**
 * Fetch the current user's server-authoritative balance. Pass `since` (epoch ms or ISO) to
 * ALSO get the topup-since fact `{ topupSinceCenti, lastTopupTs }` — the UNSPOOFABLE backstop
 * that fires the topup-success confirmation when the live reason:'topup' socket emit is dropped
 * by the post-redirect socket-replay hole. No client baseline is ever sent (never a diff).
 */
export async function getCreditsBalance(opts?: { since?: string | number }): Promise<CreditsBalanceView> {
  const qs = new URLSearchParams();
  if (opts?.since != null) qs.set('since', String(opts.since));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authFetch(`/api/billing/credits/balance${suffix}`);
  if (!res.ok) throw new Error(`Failed to load credits balance: ${res.status}`);
  return res.json();
}

/** Fetch the current user's paid top-up receipts (newest first). */
export async function getCreditsReceipts(): Promise<CreditsReceiptsResponse> {
  const res = await authFetch('/api/billing/credits/receipts');
  if (!res.ok) throw new Error(`Failed to load receipts: ${res.status}`);
  return res.json();
}

/**
 * Fetch the current user's credits-spend usage rollup (cascade_cost_log DATE_TRUNC
 * buckets + cascade-savings meter). Mirrors backend UsageRollup.
 */
export async function getCreditsUsage(opts?: { period?: string; days?: number }): Promise<unknown> {
  const qs = new URLSearchParams();
  if (opts?.period) qs.set('period', opts.period);
  if (opts?.days != null) qs.set('days', String(opts.days));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await authFetch(`/api/billing/credits/usage${suffix}`);
  if (!res.ok) throw new Error(`Failed to load credits usage: ${res.status}`);
  return res.json();
}

/**
 * Self-serve refund of a fully-unconsumed top-up lot (paid + linked bonus) bought this
 * Sofia month → `POST /billing/credits/refund`. The backend issues the Stripe refund and
 * writes the compensating ledger rows; the storno is drawn ONLY by the CaaS webhook. On a
 * policy rejection the backend returns a 4xx with `{ refunded:false, reason }` — surfaced here.
 */
export async function refundCreditsLot(lotId: string): Promise<{ refunded: boolean; reason?: string }> {
  const res = await authFetch('/api/billing/credits/refund', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lotId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok && res.status >= 500) {
    throw new Error(`Refund failed: ${res.status}`);
  }
  return body;
}
